import React, { useState, useEffect, useMemo } from "react";
import styles from "./SambaFilePickerModal.module.css";
import { MyIcons } from "../icons/my-icons";
import Button from "../ui/button";
import { API_BASE_URL } from "../../config";

const SambaFilePickerModal = ({ isOpen, onClose, onFilesSelect, expId, expedienteYear, multiple = true, initialSelectedFiles = [], initialPath = "" }) => {
  // Limpiar el expedienteId de espacios al inicio
  const cleanExpId = expId ? expId.toString().replace(/\s+/g, '') : '';
  
  const [currentFolder, setCurrentFolder] = useState("");
  const [items, setItems] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewText, setPreviewText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  // Función para formatear tamaños de archivo
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Inicializar la carpeta actual con initialPath cuando se abre el modal
  useEffect(() => {
    if (isOpen && initialPath) {
      setCurrentFolder(initialPath);
    } else if (isOpen) {
      setCurrentFolder("");
    }
  }, [isOpen, initialPath]);

  // Crear una clave única basada en el contenido de initialSelectedFiles para detectar cambios
  const initialSelectedKey = useMemo(() => {
    if (!initialSelectedFiles || initialSelectedFiles.length === 0) return 'empty';
    return initialSelectedFiles.map(item => {
      if (typeof item === 'string') return item;
      if (item && typeof item === 'object' && item.path) return item.path;
      return '';
    }).sort().join('|');
  }, [initialSelectedFiles]);

  // Inicializar archivos seleccionados cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      // Convertir rutas SAMBA completas a objetos de archivo con fullPath
      const filesFromPaths = (initialSelectedFiles || []).map(item => {
        // Extraer el path como string
        let sambaPath;
        if (typeof item === 'string') {
          sambaPath = item;
        } else if (item && typeof item === 'object' && item.path) {
          sambaPath = item.path;
        } else {
          return null; // Skip invalid items
        }
        
        // Validar que sambaPath es un string
        if (!sambaPath || typeof sambaPath !== 'string') {
          return null;
        }
        
        // sambaPath puede venir en varios formatos:
        // 1. Path completo SAMBA: /srv/samba/shared/Expedientes/{year}/{expId}/{fullPath}
        // 2. Path SAMBA sin año: /srv/samba/shared/Expedientes/{expId}/{fullPath}
        // 3. Path relativo con año/expId: {year}/{expId}/{fullPath}
        // 4. Path relativo sin prefijo: {carpeta}/{archivo}
        
        let fullPath;
        
        // Caso 1: Path completo con prefijo SAMBA y año
        const fullPrefixWithYear = `/srv/samba/shared/Expedientes/${expedienteYear}/${cleanExpId}/`;
        if (sambaPath.startsWith(fullPrefixWithYear)) {
          fullPath = sambaPath.substring(fullPrefixWithYear.length);
        }
        // Caso 2: Path completo con prefijo SAMBA sin año
        else if (sambaPath.startsWith(`/srv/samba/shared/Expedientes/${cleanExpId}/`)) {
          fullPath = sambaPath.substring(`/srv/samba/shared/Expedientes/${cleanExpId}/`.length);
        }
        // Caso 3: Path relativo que empieza con año/expId
        else if (sambaPath.startsWith(`${expedienteYear}/${cleanExpId}/`)) {
          fullPath = sambaPath.substring(`${expedienteYear}/${cleanExpId}/`.length);
        }
        // Caso 4: Path ya es relativo (carpeta/archivo)
        else {
          fullPath = sambaPath;
        }
        
        if (!fullPath || typeof fullPath !== 'string') {
          return null;
        }
        
        const fileName = fullPath.split('/').pop();
        return { name: fileName, fullPath, type: 'file' };
      }).filter(file => file !== null); // Remove null items
      
      console.log('Archivos pre-seleccionados inicializados:', filesFromPaths);
      setSelectedFiles(filesFromPaths);
    }
  }, [isOpen, expedienteYear, cleanExpId, initialSelectedKey]);

  // Cargar archivos y carpetas del SAMBA
  useEffect(() => {
    if (!isOpen) return;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('authToken');
        const url = `${API_BASE_URL}/archivos/${cleanExpId}${currentFolder ? `?path=${encodeURIComponent(currentFolder)}` : ''}`;
        
        const res = await fetch(url, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {}
        });
        
        if (!res.ok) {
          throw new Error(`Error al obtener archivos: ${res.status}`);
        }
        
        const data = await res.json();
        setItems(data);
      } catch (err) {
        console.error('Error en fetch:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [cleanExpId, currentFolder, isOpen]);

  const getFileIcon = (fileName, isSelected = false) => {
    if (!fileName || typeof fileName !== 'string') {
      const color = isSelected ? "white" : "var(--dark-grey)";
      return <MyIcons name="fileFilled" width={24} height={24} color={color} />;
    }
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const color = isSelected ? "white" : "var(--dark-grey)";
    const mainColor = isSelected ? "white" : "var(--main)";
    
    if (ext === "pdf") return <MyIcons name="pdf" width={24} height={24} color={color} />;
    if (["jpg","jpeg","png","gif","webp","bmp","svg"].includes(ext)) return <MyIcons name="image" width={24} height={24} color={color} />;
    if (["doc","docx"].includes(ext)) return <MyIcons name="fileFilled" width={24} height={24} color={mainColor} />;
    return <MyIcons name="fileFilled" width={24} height={24} color={color} />;
  };

  const handleFolderClick = (folderName) => {
    setCurrentFolder(currentFolder ? `${currentFolder}/${folderName}` : folderName);
  };

  const handleBackClick = () => {
    if (!currentFolder) return;
    const parts = currentFolder.split('/');
    parts.pop();
    setCurrentFolder(parts.join('/'));
  };

  const handleFileClick = (file) => {
    // Agregar timestamp para evitar problemas de caché con archivos del mismo nombre
    const timestamp = new Date().getTime();
    
    // Crear el path completo del archivo (usado para identificación única)
    const fullPath = currentFolder ? `${currentFolder}/${file.name}` : file.name;
    
    // Set preview for clicked file (show preview even if toggling selection)
    const fileUrlCandidates = [];
    // Prefer any provided URL from the backend
    // Try /api/files/serve endpoint with query parameter (works better with special characters)
    try {
      const relativePath = currentFolder ? `${currentFolder}/${file.name}` : file.name;
      // Calculate year from expediente ID
      const expedienteYear = Math.floor(parseInt(cleanExpId) / 10000) + 2000;
      const fullPath = `${expedienteYear}/${cleanExpId}/${relativePath}`;
      // Encode the full path as query parameter
      const encodedPath = encodeURIComponent(fullPath);
      const apiUrl = `${API_BASE_URL}/api/files/serve?path=${encodedPath}&t=${timestamp}`;
      // Try the constructed API URL first
      fileUrlCandidates.push(apiUrl);
      if (file.url) {
        const urlWithTimestamp = `${file.url}${file.url.includes('?') ? '&' : '?'}t=${timestamp}`;
        fileUrlCandidates.push(urlWithTimestamp);
      }
    } catch (e) {
      // ignore encoding errors
      if (file.url) {
        const urlWithTimestamp = `${file.url}${file.url.includes('?') ? '&' : '?'}t=${timestamp}`;
        fileUrlCandidates.push(urlWithTimestamp);
      }
    }
    const preview = { ...file, fullPath, possibleUrls: fileUrlCandidates, previewUrl: fileUrlCandidates[0] };
    setPreviewFile(preview);
    setPreviewBlobUrl(null);
    setPreviewLoading(false);
    setPreviewError(null);

    if (!multiple) {
      // Modo single: solo permite seleccionar un archivo
      setSelectedFiles([{ ...file, fullPath }]);
    } else {
      // Modo multiple: toggle selection usando fullPath como identificador único
      const isSelected = selectedFiles.some(f => f.fullPath === fullPath);
      if (isSelected) {
        setSelectedFiles(selectedFiles.filter(f => f.fullPath !== fullPath));
      } else {
        setSelectedFiles([...selectedFiles, { ...file, fullPath }]);
      }
    }
  };

  // When previewFile changes, try fetching the possible urls (with auth) and create blob URL for preview
  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const loadPreview = async () => {
      if (!previewFile) return;
      setPreviewLoading(true);
      setPreviewError(null);

      const urls = previewFile.possibleUrls && previewFile.possibleUrls.length > 0
        ? previewFile.possibleUrls
        : (previewFile.url ? [previewFile.url] : []);

      for (let i = 0; i < urls.length; i++) {
        const currentUrl = urls[i];
        try {
          console.log('Samba preview: intentando URL de previsualización ->', currentUrl);
          const token = localStorage.getItem('authToken');
          const res = await fetch(currentUrl, {
            headers: token ? { 'Authorization': `Bearer ${token}` } : {}
          });
          console.log('Samba preview: respuesta status ->', res.status);

          if (!res.ok) {
            // try next URL
            continue;
          }

          const blob = await res.blob();

          // If it's a text file, read as text and set previewText instead of blob URL
          const ext = (previewFile.name && typeof previewFile.name === 'string') ? previewFile.name.split('.').pop()?.toLowerCase() || '' : '';
          const isTextMime = blob.type && blob.type.startsWith('text');
          const isTxtExt = ext === 'txt' || ext === 'md' || ext === 'csv';

          if (isTextMime || isTxtExt) {
            const text = await blob.text();
            if (!mounted) return;
            setPreviewText(text);
            // ensure we clear any previous blob URL
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
              objectUrl = null;
            }
            setPreviewBlobUrl(null);
            setPreviewLoading(false);
            setPreviewError(null);
            return;
          }

          // Non-text: create object URL for image/pdf preview
          objectUrl = URL.createObjectURL(blob);
          if (!mounted) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          setPreviewBlobUrl(objectUrl);
          setPreviewText(null);
          setPreviewLoading(false);
          setPreviewError(null);
          return;
        } catch (err) {
          console.error('Error loading preview url:', currentUrl, err);
          continue;
        }
      }

      if (mounted) {
        setPreviewError('No se pudo cargar la previsualización');
        setPreviewLoading(false);
      }
    };

    loadPreview();

    return () => {
      mounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previewFile]);

  const handleConfirm = () => {
    if (selectedFiles.length === 0) return;

    // Construir objetos con ruta, nombre y tamaño para cada archivo seleccionado
    const fileObjects = selectedFiles.map((file) => {
      const sambaPath = `${expedienteYear}/${cleanExpId}/${file.fullPath}`;
      return {
        path: sambaPath,
        name: file.name,
        size: file.size || 0
      };
    });

    console.log('Archivos seleccionados:', fileObjects);
    onFilesSelect(fileObjects);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setCurrentFolder("");
    setSearchValue("");
    setError(null);
    onClose();
  };

  const isFileSelected = (file) => {
    const fullPath = currentFolder ? `${currentFolder}/${file.name}` : file.name;
    return selectedFiles.some(f => f.fullPath === fullPath);
  };

  const renderContent = () => {
    if (loading) return <div className={styles.noFiles}>Cargando...</div>;
    if (error) return <div className={styles.noFiles}>Error: {error}</div>;
    
    let folders = [];
    let files = [];
    
    if (Array.isArray(items)) {
      folders = items.filter(item => item.type === 'directory' || item.isDirectory);
      files = items.filter(item => item.type === 'file' || item.isFile || (!item.type && !item.isDirectory));
    }
    
    const filteredFiles = searchValue
      ? files.filter((file) => file.name.toLowerCase().includes(searchValue.toLowerCase()))
      : files;

    return (
      <>
        {currentFolder && (
          <div className={styles.folderHeader}>
            <button className={styles.backButton} onClick={handleBackClick}>
              <MyIcons
                name="arrowLeft"
                width={24}
                height={24}
                color={"var(--black)"}
              />
            </button>
            <h2 className={styles.folderTitle}>
              <MyIcons
                name="folder"
                width={24}
                height={24}
                color={"var(--main)"}
              />
              {currentFolder}
            </h2>
          </div>
        )}

        {folders.length > 0 && (
          <div className={styles.foldersGrid}>
            {folders.map((folder) => (
              <div
                key={folder.name}
                className={styles.folderItem}
                onClick={() => handleFolderClick(folder.name)}
              >
                <div>
                  <MyIcons
                    name="folder"
                    width={48}
                    height={48}
                    color={"var(--main)"}
                  />
                </div>
                <div className={styles.folderName}>{folder.name}</div>
                {folder.count && (
                  <div className={styles.fileCount}>
                    {folder.count} archivos
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className={styles.filesAndPreview}>
          <div className={styles.filesColumn}>
            {items.length === 0 ? (
          <div className={styles.noFiles}>
            No se encontraron archivos
          </div>
            ) : (
              <div className={styles.filesGrid}>
                {filteredFiles.map((file) => (
                  <div
                    key={file.name}
                    className={`${styles.fileItem} ${isFileSelected(file) ? styles.fileSelected : ''}`}
                    onClick={() => handleFileClick(file)}
                  >
                    <div className={styles.fileIconContainer}>
                      {getFileIcon(file.name, isFileSelected(file))}
                    </div>
                    <div>
                      <div className={styles.fileName}>{file.name}</div>
                      {file.size && (
                        <div className={styles.fileSize}>{formatFileSize(file.size)}</div>
                      )}
                    </div>
                    {isFileSelected(file) && (
                      <div className={styles.checkMark}>
                        <MyIcons name="check" width={16} height={16} color="white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={styles.previewColumn}>
            {previewFile ? (
              <div className={styles.previewContainer}>
                {previewLoading && <div className={styles.previewLoading}>Cargando previsualización...</div>}
                {previewError && <div className={styles.previewError}>{previewError}</div>}
                {!previewLoading && !previewError && (() => {
                  const ext = (previewFile.name && typeof previewFile.name === 'string') ? previewFile.name.split('.').pop()?.toLowerCase() || '' : '';
                  const url = previewBlobUrl || previewFile.previewUrl || previewFile.url;
                  if (["jpg","jpeg","png","gif","webp","bmp","svg"].includes(ext)) {
                    return <img src={url} alt={previewFile.name} className={styles.previewImage} />;
                  }
                  if (ext === 'pdf') {
                    return <iframe src={url} title={previewFile.name} className={styles.previewPdf} />;
                  }
                  if (previewText) {
                    return (
                      <div className={styles.previewTextContainer}>
                        <pre className={styles.previewText}>{previewText}</pre>
                      </div>
                    );
                  }
                  // Fallback: show file info (no download link) and message
                  return (
                    <div className={styles.previewFallback}>
                      <div className={styles.previewFileName}>{previewFile.name}</div>
                      <div className={styles.previewNoAvailable}>
                        Previsualización no disponible para este tipo de archivo
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className={styles.previewPlaceholder}>Selecciona un archivo para ver la previsualización</div>
            )}
          </div>
        </div>
      </>
    );
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Seleccionar archivos</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <MyIcons name="close" width={24} height={24} color="var(--black)" />
          </button>
        </div>

        <div className={styles.searchContainer}>
          <input
            type="text"
            className={styles.searchInput}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Buscar archivos..."
          />
        </div>

        <div className={styles.modalBody}>
          {renderContent()}
        </div>

        <div className={styles.modalFooter}>
          <div className={styles.selectedCount}>
            {selectedFiles.length} archivo{selectedFiles.length !== 1 ? 's' : ''} seleccionado{selectedFiles.length !== 1 ? 's' : ''}
          </div>
          <div className={styles.buttonGroup}>
            <Button
              onClick={handleClose}
              variant="secondary"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={selectedFiles.length === 0}
              variant="primary"
            >
              Confirmar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SambaFilePickerModal;
